/**
 * Publishes refreshed Firebase ID tokens to the local desktop backend.
 *
 * The backend forwards the signed-in user's credential to Cloud Run for every
 * billed operation. A Firebase ID token lives one hour, and desktop work
 * routinely outlives it — a 30-minute cloud poll loop, a 40-minute generation
 * pipeline, an hours-long dub. Requests the renderer makes already carry a fresh
 * token and the backend harvests those, but a long job can run while the UI sits
 * idle making no requests at all. This closes that window: whenever Firebase
 * hands the renderer a new token, the backend hears about it.
 *
 * Desktop only. In web mode the browser talks to Cloud Run directly, so there is
 * no intermediary holding a credential on the user's behalf.
 */

import { isElectronMode } from '../utils/apiMode';
import { buildApiUrl } from './urlResolver';
import { getAuthHeaders } from '../utils/httpUtils';

/** Local endpoint that records the credential (api_server/local_routes/auth_session.py). */
const CREDENTIAL_SESSION_ENDPOINT = '/api/v1/auth/session/token';

/**
 * Hand a freshly minted ID token to the local backend.
 *
 * The token travels in the `Authorization` header, not the body — it is a
 * credential, and that is where every other route on that server reads one.
 *
 * Failures are swallowed deliberately: this is an optimisation for a job that
 * may not even be running, and the backend still gets a fresh token from the
 * next ordinary request. Surfacing a toast because a background push missed
 * would be noise about a problem the user cannot act on.
 *
 * @param idToken - The Firebase ID token to publish, or null when signed out.
 */
export async function publishCredentialToLocalBackend(idToken: string | null): Promise<void> {
  if (!isElectronMode() || !idToken) {
    return;
  }

  try {
    await fetch(buildApiUrl(CREDENTIAL_SESSION_ENDPOINT), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
    });
  } catch (err) {
    console.warn(
      '[CredentialSession] Could not publish the refreshed token to the local backend:',
      err
    );
  }
}
